// Fake contact shadows — dark radial-gradient quads under props, units and
// cities. Real shadow maps can't land on this board: terrain and scatter are
// unlit by design (baked vertex shading, see TerrainScatterMaterials' header
// and the #122 audit), so a shadow-casting light has nothing to receive on
// the ground. A grounded blob under every object is the standard low-poly
// alternative and costs one instanced draw per group.
//
// The quads sit just above the surface with depthWrite off and render after
// the terrain (before the hex outlines), so they tint the ground without
// fighting it or softening the grid lines.

import type { SceneNode } from '@aod/protocol';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { tileElevationAt, waterSurfaceY } from './elevation.js';

/** Drawn after terrain, before the hex outlines (Terrain + 0.1). */
export const SHADOW_RENDER_ORDER = RenderLayer.Terrain + 0.05;
/** Clearance above the surface so the quad never z-fights the tile top. */
const SHADOW_LIFT = 0.02;

let shadowTexture: THREE.Texture | null = null;

/** Radial-falloff alpha blob on a canvas, cached at module level. */
function getShadowTexture(): THREE.Texture {
  if (shadowTexture) return shadowTexture;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(0,0,0,0.85)');
  grad.addColorStop(0.55, 'rgba(0,0,0,0.5)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  shadowTexture = tex;
  return tex;
}

let shadowGeo: THREE.PlaneGeometry | null = null;

function getShadowGeometry(): THREE.PlaneGeometry {
  if (!shadowGeo) {
    shadowGeo = new THREE.PlaneGeometry(1, 1);
    shadowGeo.rotateX(-Math.PI / 2);
  }
  return shadowGeo;
}

export interface ShadowSpot {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Full diameter of the blob, world units. */
  readonly d: number;
  /** 0..1 — multiplies the shared material opacity for subtle variation. */
  readonly strength: number;
}

// One shared material: dark, translucent, fog-aware, no depth writes.
const shadowMaterial = new THREE.MeshBasicMaterial({
  transparent: true,
  depthWrite: false,
  opacity: 0.32,
  color: new THREE.Color('#0a120a'),
});

/**
 * A batch of shadow blobs as one InstancedMesh. `spots` identity drives the
 * matrix fill — rebuild the array (memoized) when the world changes, not
 * per frame.
 */
export function ShadowSpots({ spots }: { spots: ReadonlyArray<ShadowSpot> }) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const material = useMemo(() => {
    shadowMaterial.map = getShadowTexture();
    shadowMaterial.needsUpdate = true;
    return shadowMaterial;
  }, []);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const obj = new THREE.Object3D();
    for (let i = 0; i < spots.length; i++) {
      const s = spots[i]!;
      obj.position.set(s.x, s.y, s.z);
      obj.rotation.set(0, 0, 0);
      obj.scale.set(s.d, 1, s.d);
      obj.updateMatrix();
      mesh.setMatrixAt(i, obj.matrix);
      // Per-instance fade via instanceColor (tints the dark blob).
      mesh.setColorAt(i, new THREE.Color(s.strength, s.strength, s.strength));
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.count = spots.length;
    mesh.computeBoundingSphere();
  }, [spots]);

  if (spots.length === 0) return null;
  return (
    <instancedMesh
      ref={meshRef}
      name="contact-shadows"
      args={[getShadowGeometry(), material, spots.length]}
      renderOrder={SHADOW_RENDER_ORDER}
      frustumCulled={false}
    />
  );
}

// ── Spot builders ────────────────────────────────────────────────────────

const WATER_BIOMES = new Set(['ocean', 'coast', 'river']);

/** Surface Y a ground blob should sit at for a tile (land top or water plane). */
export function shadowSurfaceY(q: number, r: number, biomeMap: Map<string, string>): number {
  const biome = biomeMap.get(`${q},${r}`) ?? 'plains';
  if (WATER_BIOMES.has(biome)) return waterSurfaceY(q, r, biomeMap) + SHADOW_LIFT;
  return tileElevationAt(q, r, biomeMap) + SHADOW_LIFT;
}

function nodeCoords(node: SceneNode): { x: number; z: number; q: number; r: number } {
  const props = node.props as Record<string, unknown> | undefined;
  return {
    x: node.transform.position[0] ?? 0,
    z: node.transform.position[2] ?? 0,
    q: (props?.q as number) ?? 0,
    r: (props?.r as number) ?? 0,
  };
}

/** One blob under each unit (boat hulls included — the water reads as depth). */
export function buildUnitShadowSpots(
  units: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): ShadowSpot[] {
  return units.map((u) => {
    const { x, z, q, r } = nodeCoords(u);
    return { x, y: shadowSurfaceY(q, r, biomeMap), z, d: 0.42, strength: 0.8 };
  });
}

/** One broad blob under each city's building cluster. */
export function buildCityShadowSpots(
  cities: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): ShadowSpot[] {
  return cities.map((c) => {
    const { x, z, q, r } = nodeCoords(c);
    return { x, y: shadowSurfaceY(q, r, biomeMap) + 0.004, z, d: 1.05, strength: 0.9 };
  });
}
